import { Flex } from "@amzn/storm-ui";
import { useAsync } from "@react-hookz/web";
import React, { useEffect, useMemo, type ReactElement } from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import { getBlinkApiClientV2 } from "../../../api/blinkApi/blinkApi";
import { useAsyncErrorStatusNotification } from "../../../hooks/notifications/useAsyncErrorStatusNotification";
import { navigationStore } from "../../../store/navigation";
import { salesforceStore } from "../../../store/salesforce";
import { isLoading } from "../../../utils/api.utils";
import {
    getAdvertiserId,
    getAdvertiserName,
    isValidAdvertiserId,
} from "../../../utils/permission.utils";
import { isAllInsightsTab } from "../../../utils/tab.utils";
import { AccountSwitcher } from "../../customAccountPicker/AccountSwitcher";

import { ContextItem } from "./ContextItem";

const StyledSfContextWrapper = styled(Flex)`
    margin-top: 12px;
    width: 100%;
`;

export const SfContext = (): ReactElement | null => {
    const advertiserId = getAdvertiserId();
    const advertiserName = getAdvertiserName();
    const isValidId = isValidAdvertiserId();

    const [sfContext, setSfContext, setAsyncStatus] = useStore(
        salesforceStore,
        (state) => [state.sfContext, state.setSfContext, state.setAsyncStatus],
    );
    const [activeTab] = useStore(navigationStore, (state) => [state.activeTab]);

    const { name, locale, vertical, subvertical } = sfContext;

    const [sfState, sfAction] = useAsync(async () => {
        const { data } = await (
            await getBlinkApiClientV2()
        ).getSfContext({
            advertiserId,
        });

        if (data) {
            setSfContext(data);
        }
    });
    useEffect(() => setAsyncStatus(sfState.status), [sfState.status]);

    const loading = isLoading(sfState.status);

    useAsyncErrorStatusNotification(sfState);

    useEffect(() => {
        if (isValidId) {
            void sfAction.execute();
        }
    }, [isValidId]);

    const contextItems = useMemo(() => {
        return isAllInsightsTab(activeTab)
            ? [
                  {
                      isLoading: loading,
                      label: "Marketplace",
                      value: locale,
                  },
                  {
                      isLoading: loading,
                      label: "Vertical",
                      value: vertical,
                  },
                  {
                      isLoading: loading,
                      label: "Sub Vertical",
                      value: subvertical,
                  },
              ]
            : [];
    }, [loading, locale, vertical, subvertical, activeTab]);

    return (
        <StyledSfContextWrapper
            direction="column"
            rowGap="small"
            data-testid="sf-context"
        >
            <Flex alignItems="center" justifyContent="space-between" fullWidth>
                <Flex alignItems="center" columnGap="xxxsmall">
                    <span style={{ fontSize: "24px" }}>Account:</span>
                    <AccountSwitcher />
                </Flex>
            </Flex>
            {isValidId && (
                <Flex columnGap="base" wrap="wrap" alignItems="center">
                    {contextItems.map(({ isLoading, label, value }) => (
                        <ContextItem
                            key={label}
                            isLoading={isLoading}
                            label={label}
                            value={value}
                        />
                    ))}
                </Flex>
            )}
        </StyledSfContextWrapper>
    );
};
